/**
 * Player API Common Schemas
 *
 * Shared schemas used across Player API requests and responses.
 * Includes common entities like countries, regions, pricing, and messages.
 */

import { z } from 'zod';

// ----- Message and Error Schemas -----

export const ProcessMessageTypeSchema = z.enum(['Error', 'ConfigurationError', 'Information']);

export const ErrorCodeSchema = z.enum(['UnspecificError', 'ValidationError']);

export const ProcessMessageSchema = z.object({
  MessageType: ProcessMessageTypeSchema.optional(),
  ProcessIdentifier: z.string().optional(),
  MessageCode: z.string().optional(),
  Message: z.string().optional(),
});

export const ErrorMessageSchema = z.object({
  ErrorCode: ErrorCodeSchema.optional(),
  ErrorMessage: z.string().nullable().optional(),
  PlayerName: z.string().nullable().optional(),
});

// ----- Geographic Entities -----

export const CountrySchema = z.object({
  Code: z.string(),
  Name: z.string(),
});

export const RegionSchema = z.object({
  Code: z.string(),
  Name: z.string(),
});

export const DestinationSchema = z.object({
  Code: z.string(),
  Name: z.string(),
});

export const CitySchema = z.object({
  Name: z.string(),
});

// ----- Service Identifiers -----

export const IdentitySchema = z.object({
  IdentityProvider: z.string(),
  ServiceIdentity: z.string(),
});

/**
 * Service reference with passenger assignment for multi-room support
 */
export const ServiceReferenceSchema = z.object({
  ServiceId: z.number(),
  ServiceVariantId: z.number(),

  /**
   * Passenger IDs assigned to this service reference
   * Present in multi-room responses to identify which room this offer belongs to
   *
   * ⚠️ IMPORTANT: MUST BE OPTIONAL
   * Player API does NOT return this field for single-room responses.
   * This is an external API constraint that cannot be changed.
   *
   * @example { PassengerId: [1, 2] } - Room with passengers 1 and 2
   */
  PassengerIds: z
    .object({
      PassengerId: z.array(z.number()),
    })
    .optional(), // ← CRITICAL: Must be optional due to API behavior
});

/**
 * Detailed passenger information with type and age
 * Used for DaVinci availability checks and multi-room service creation
 *
 * Integrated from Phase 2.5 - preparing Phase 2 for immediate Phase 3 readiness
 */
export interface PassengerInfo {
  /**
   * Passenger ID (1-based, matches Player API IDs)
   * Sequential numbering across all rooms
   * @example Room 1: IDs 1,2,3 | Room 2: IDs 4,5
   */
  id: number;

  /**
   * Passenger type (adult or child)
   */
  type: 'adult' | 'child';

  /**
   * Age of child (required if type is 'child')
   * @example 4, 10, 17
   */
  age?: number;

  /**
   * The hotel's Adult/Child/Infant classification (from Player PassengerCode).
   * Kept separate from the binary `type` so an under-18 hotel-adult can be
   * `type: 'child'` (age < 18) while `hotelClass: 'adult'`.
   */
  hotelClass?: 'adult' | 'child' | 'infant';
}

// ----- Pricing Schemas -----

export const PriceInformationPassengerSchema = z.object({
  Id: z.number(),
  Amount: z.number(),
  PassengerCode: z.enum(['Adult', 'Child', 'Infant']),
});

// PriceDetail describes individual discount/surcharge components
export const PriceDetailSchema = z.object({
  PriceType: z.string(), // e.g., "EarlyBird", "ChildReduction", etc.
  Amount: z.number(), // Discount amount (negative for discounts, positive for surcharges)
  Description: z.string().optional(),
});

export const PriceInformationSchema = z.object({
  Amount: z.number(),
  PerPerson: z.boolean(),
  Passengers: z.object({
    PriceInformationPassenger: z.array(PriceInformationPassengerSchema),
  }),
  PriceTypes: z
    .object({
      PriceType: z.array(z.string()),
    })
    .optional(),
  // PriceDetails is only included if DetailLevel is "PriceDetails" or "PriceDetailsPerPerson"
  PriceDetails: z
    .object({
      PriceDetail: z.array(PriceDetailSchema),
    })
    .optional(),
});

// ----- Type Exports -----

export type ProcessMessageType = z.infer<typeof ProcessMessageTypeSchema>;
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;
export type ProcessMessage = z.infer<typeof ProcessMessageSchema>;
export type ErrorMessage = z.infer<typeof ErrorMessageSchema>;
export type Country = z.infer<typeof CountrySchema>;
export type Region = z.infer<typeof RegionSchema>;
export type Destination = z.infer<typeof DestinationSchema>;
export type City = z.infer<typeof CitySchema>;
export type Identity = z.infer<typeof IdentitySchema>;
export type ServiceReference = z.infer<typeof ServiceReferenceSchema>;
export type PriceInformationPassenger = z.infer<typeof PriceInformationPassengerSchema>;
export type PriceDetail = z.infer<typeof PriceDetailSchema>;
export type PriceInformation = z.infer<typeof PriceInformationSchema>;
